<template>
  <div class="drama-management">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>短剧管理</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="handleAdd">
          <i class="el-icon-plus"></i> 新增短剧
        </el-button>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar" style="margin-bottom: 20px;">
        <el-select
          v-model="searchCategoryId"
          placeholder="选择分类"
          clearable
          filterable
          style="width: 200px; margin-right: 10px;"
          @change="handleSearch"
        >
          <el-option
            v-for="item in categoryList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          >
          </el-option>
        </el-select>
        <el-input
          v-model="searchKeyword"
          placeholder="请输入短剧名称搜索"
          prefix-icon="el-icon-search"
          clearable
          style="width: 300px;"
          @clear="handleSearch"
          @keyup.enter.native="handleSearch"
        >
        </el-input>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch" style="margin-left: 10px;">搜索</el-button>
        <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 数据表格 -->
      <el-table
        :data="dramaList"
        border
        stripe
        v-loading="loading"
        style="width: 100%"
      >
        <el-table-column prop="id" label="ID" width="80" align="center"></el-table-column>
        <el-table-column label="封面" width="120" align="center">
          <template slot-scope="scope">
            <el-image 
              v-if="scope.row.coverUrl || scope.row.coverImage"
              :src="scope.row.coverUrl || scope.row.coverImage" 
              fit="cover"
              style="width: 80px; height: 60px; border-radius: 4px;"
              :preview-src-list="[scope.row.coverUrl || scope.row.coverImage]"
            >
            </el-image>
            <span v-else>无封面</span>
          </template>
        </el-table-column>
        <el-table-column prop="title" label="短剧名称" min-width="150">
          <template slot-scope="scope">
            {{ scope.row.title || scope.row.name }}
          </template>
        </el-table-column>
        <el-table-column prop="description" label="简介" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.description">{{ scope.row.description }}</span>
            <span v-else style="color: #909399;">暂无简介</span>
          </template>
        </el-table-column>
        <el-table-column prop="totalEpisodes" label="总集数" width="100" align="center"></el-table-column>
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
              {{ scope.row.status === 1 ? '上架' : '下架' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180" align="center">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.createdAt || scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="320" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="primary" icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="success" icon="el-icon-video-camera" @click="handleManageEpisodes(scope.row)">集数管理</el-button>
            <el-button size="mini" type="danger" icon="el-icon-delete" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
        style="margin-top: 20px; text-align: right;"
      >
      </el-pagination>
    </el-card>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="700px"
      @close="handleDialogClose"
    >
      <el-form ref="dramaForm" :model="dramaForm" :rules="dramaRules" label-width="100px">
        <el-form-item label="短剧名称" prop="title">
          <el-input 
            v-model="dramaForm.title" 
            placeholder="请输入短剧标题(必填)"
            maxlength="100"
            show-word-limit
          ></el-input>
          <div class="form-tip" style="color: #909399; font-size: 12px; margin-top: 5px;">
            ℹ️ 短剧标题,最多100字符
          </div>
        </el-form-item>
        <el-form-item label="所属分类" prop="categoryId">
          <el-select
            v-model="dramaForm.categoryId"
            placeholder="请选择分类(必填)"
            filterable
            style="width: 100%;"
          >
            <el-option
              v-for="item in categoryList"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="封面图片" prop="coverUrl">
          <el-upload
            class="cover-uploader"
            :action="uploadCoverUrl"
            :headers="uploadHeaders"
            :on-success="handleCoverSuccess"
            :on-error="handleUploadError"
            :before-upload="beforeCoverUpload"
            :show-file-list="false"
            accept="image/*"
          >
            <img v-if="dramaForm.coverUrl" :src="dramaForm.coverUrl" class="cover-image">
            <i v-else class="el-icon-plus cover-uploader-icon"></i>
          </el-upload>
          <div class="upload-tip">建议尺寸: 750x1000px, 支持jpg/png格式</div>
        </el-form-item>
        <el-form-item label="简介" prop="description">
          <el-input 
            v-model="dramaForm.description" 
            type="textarea" 
            :rows="3"
            placeholder="选填,请输入短剧简介"
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>
        <el-form-item label="总集数" prop="totalEpisodes">
          <el-input-number 
            v-model="dramaForm.totalEpisodes" 
            :min="0" 
            :max="9999"
            placeholder="选填,默认0"
            style="width: 100%;"
          ></el-input-number>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="dramaForm.status">
            <el-radio :label="1">上架</el-radio>
            <el-radio :label="0">下架</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { request, BASE_URL } from '@/utils/http.js'

export default {
  name: 'DramaManagement',
  data() {
    return {
      loading: false,
      submitLoading: false,
      searchCategoryId: null,
      searchKeyword: '',
      dramaList: [],
      categoryList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '新增短剧',
      dramaForm: {
        id: null,
        title: '',
        categoryId: null,
        coverUrl: '',
        description: '',
        totalEpisodes: 0,
        status: 1
      },
      dramaRules: {
        title: [
          { required: true, message: '请输入短剧标题', trigger: 'blur' },
          { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        categoryId: [
          { required: true, message: '请选择分类', trigger: 'change' }
        ],
        coverUrl: [
          { required: true, message: '请上传封面图片', trigger: 'change' }
        ]
      },
      uploadCoverUrl: `${BASE_URL}/admin/drama/upload/cover`,
      uploadHeaders: {
        'Authorization': localStorage.getItem('token') || ''
      }
    }
  },
  mounted() {
    this.loadCategoryList()
    this.loadDramaList()
  },
  methods: {
    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      return dateTime.replace('T', ' ')
    },
    
    // 加载分类列表
    async loadCategoryList() {
      console.log('=== 开始加载分类列表 ===');
      console.log('当前token:', localStorage.getItem('token'));
      
      try {
        const response = await request('/admin/drama/category/list', { 
          method: 'GET',
          params: { pageNum: 1, pageSize: 1000 }
        })
        
        console.log('分类列表响应:', response);
        
        if (response.code === 200 && response.data) {
          this.categoryList = response.data.records || []
          console.log('分类列表加载成功,数量:', this.categoryList.length);
        } else {
          console.error('分类列表加载失败,响应码:', response.code, '消息:', response.msg);
        }
      } catch (error) {
        console.error('加载分类列表失败:', error)
      }
    },

    // 加载短剧列表
    async loadDramaList() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pagination.page,
          pageSize: this.pagination.pageSize
        }
        if (this.searchCategoryId) {
          params.categoryId = this.searchCategoryId
        }
        
        const response = await request('/admin/drama/list', { 
          method: 'GET',
          params 
        })
        
        if (response.code === 200 && response.data) {
          this.dramaList = response.data.records || []
          this.pagination.total = response.data.total || 0
        } else {
          this.$message.error(response.msg || '加载失败')
        }
      } catch (error) {
        this.$message.error('加载短剧列表失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.loadDramaList()
    },

    // 重置
    handleReset() {
      this.searchCategoryId = null
      this.searchKeyword = ''
      this.pagination.page = 1
      this.loadDramaList()
    },

    // 封面上传前验证
    beforeCoverUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt2M = file.size / 1024 / 1024 < 2

      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过 2MB!')
        return false
      }
      return true
    },

    // 封面上传成功
    handleCoverSuccess(response) {
      if (response.code === 200) {
        this.dramaForm.coverUrl = response.data
        this.$message.success('封面上传成功')
      } else {
        this.$message.error(response.msg || '上传失败')
      }
    },

    // 上传失败
    handleUploadError() {
      this.$message.error('上传失败,请重试')
    },

    // 新增
    handleAdd() {
      this.dialogTitle = '新增短剧'
      this.dramaForm = {
        id: null,
        title: '',
        categoryId: null,
        coverUrl: '',
        description: '',
        totalEpisodes: 0,
        status: 1
      }
      this.dialogVisible = true
    },

    // 编辑
    handleEdit(row) {
      this.dialogTitle = '编辑短剧'
      this.dramaForm = {
        id: row.id,
        title: row.title || row.name,  // 兼容旧字段name
        categoryId: row.categoryId,
        coverUrl: row.coverUrl || row.coverImage || '',  // 兼容旧字段coverImage
        description: row.description || '',
        totalEpisodes: row.totalEpisodes || 0,
        status: row.status
      }
      this.dialogVisible = true
    },

    // 管理集数
    handleManageEpisodes(row) {
      this.$router.push({
        path: '/drama-episode',
        query: { dramaId: row.id }
      })
    },

    // 删除
    handleDelete(row) {
      this.$confirm(`确定要删除短剧"${row.name}"吗?`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await request(`/admin/drama/delete/${row.id}`, { 
            method: 'DELETE' 
          })
          
          if (response.code === 200) {
            this.$message.success('删除成功')
            this.loadDramaList()
          } else {
            this.$message.error(response.msg || '删除失败')
          }
        } catch (error) {
          this.$message.error('删除失败: ' + (error.message || '未知错误'))
        }
      }).catch(() => {})
    },

    // 提交表单
    handleSubmit() {
      this.$refs.dramaForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            const url = this.dramaForm.id 
              ? '/admin/drama/update' 
              : '/admin/drama/add'
            const method = this.dramaForm.id ? 'PUT' : 'POST'
            
            // 构建提交数据,只包含后端需要的字段
            const submitData = {
              title: this.dramaForm.title.trim(),
              categoryId: this.dramaForm.categoryId,
              coverUrl: this.dramaForm.coverUrl
            }
            
            // 可选字段,有值时才添加
            if (this.dramaForm.description && this.dramaForm.description.trim()) {
              submitData.description = this.dramaForm.description.trim()
            }
            
            if (this.dramaForm.totalEpisodes !== null && this.dramaForm.totalEpisodes !== undefined) {
              submitData.totalEpisodes = this.dramaForm.totalEpisodes
            }
            
            if (this.dramaForm.status !== null && this.dramaForm.status !== undefined) {
              submitData.status = this.dramaForm.status
            }
            
            // 编辑时需要ID
            if (this.dramaForm.id) {
              submitData.id = this.dramaForm.id
            }
            
            console.log('提交数据:', submitData)
            
            const response = await request(url, { 
              method,
              body: JSON.stringify(submitData)
            })
            
            if (response.code === 200) {
              this.$message.success(this.dramaForm.id ? '更新成功' : '新增成功')
              this.dialogVisible = false
              this.loadDramaList()
            } else {
              this.$message.error(response.msg || '操作失败')
            }
          } catch (error) {
            this.$message.error('操作失败: ' + (error.message || '未知错误'))
          } finally {
            this.submitLoading = false
          }
        }
      })
    },

    // 关闭对话框
    handleDialogClose() {
      this.$refs.dramaForm.resetFields()
    },

    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.loadDramaList()
    },

    // 页码改变
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadDramaList()
    }
  }
}
</script>

<style scoped>
.drama-management {
  padding: 20px;
}

.box-card {
  border-radius: 8px;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: "";
}

.clearfix:after {
  clear: both;
}

.search-bar {
  display: flex;
  align-items: center;
}

.cover-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  width: 148px;
  height: 148px;
}

.cover-uploader:hover {
  border-color: #409EFF;
}

.cover-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 148px;
  height: 148px;
  line-height: 148px;
  text-align: center;
}

.cover-image {
  width: 148px;
  height: 148px;
  display: block;
  object-fit: cover;
}

.upload-tip {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}
</style>
